import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function B2BPage() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    professional_category: "veterinario",
    vat_number: "",
    city: "",
    province: "",
    email: "",
    phone: "",
    message: "",
    interest: "",
    privacy_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Consenso privacy obbligatorio.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/b2b", form);
      toast.success(data.message);
      setForm({
        first_name: "", last_name: "", company: "", professional_category: "veterinario",
        vat_number: "", city: "", province: "", email: "", phone: "", message: "", interest: "",
        privacy_consent: false,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="b2b-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">Professionisti</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-brand leading-[0.98]">
            {t("b2b.title")}
          </h1>
          <p className="mt-6 text-lg text-muted-ink">{t("b2b.sub")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 lg:py-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <aside className="md:col-span-4">
          <div className="space-y-6">
            <div>
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-2">
                {lang === "en" ? "Who we look for" : "Chi cerchiamo"}
              </div>
              <ul className="space-y-1 text-brand">
                {(lang === "en"
                  ? ["Veterinarians", "Pharmacies and parapharmacies", "Pet shops", "Distributors", "Other resellers"]
                  : ["Veterinari", "Farmacie e parafarmacie", "Pet shop", "Distributori", "Altri rivenditori"]
                ).map((s) => (<li key={s}>• {s}</li>))}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-2">
                {lang === "en" ? "What we offer" : "Cosa offriamo"}
              </div>
              <ul className="space-y-1 text-muted-ink text-sm">
                {(lang === "en"
                  ? ["— Product catalog", "— Dedicated commercial terms", "— Training support on request", "— Communication materials"]
                  : ["— Catalogo prodotti", "— Condizioni commerciali dedicate", "— Supporto formativo su richiesta", "— Materiali di comunicazione"]
                ).map((s) => (<li key={s}>{s}</li>))}
              </ul>
            </div>
          </div>
        </aside>

        <form onSubmit={submit} className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="b2b-contact-form">
          <Input required placeholder={t("b2b.first_name")} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="rounded-none border-soft bg-white" data-testid="b2b-first-name" />
          <Input required placeholder={t("b2b.last_name")} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="rounded-none border-soft bg-white" data-testid="b2b-last-name" />
          <Input required placeholder={t("b2b.company")} value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-none border-soft bg-white md:col-span-2" data-testid="b2b-company" />
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">
              {t("b2b.category")}
            </label>
            <select
              value={form.professional_category}
              onChange={(e) => set("professional_category", e.target.value)}
              className="w-full h-9 border border-soft bg-white px-3 text-sm text-brand rounded-none"
              data-testid="b2b-category"
            >
              {Object.entries(t("b2b.categories")).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <Input placeholder={t("b2b.vat")} value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} className="rounded-none border-soft bg-white" />
          <Input placeholder={t("b2b.phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none border-soft bg-white" />
          <Input placeholder={t("b2b.city")} value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-none border-soft bg-white" />
          <Input placeholder={t("b2b.province")} value={form.province} onChange={(e) => set("province", e.target.value)} className="rounded-none border-soft bg-white" />
          <Input type="email" required placeholder={t("common.email")} value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none border-soft bg-white md:col-span-2" data-testid="b2b-email" />
          <Input placeholder={t("b2b.interest")} value={form.interest} onChange={(e) => set("interest", e.target.value)} className="rounded-none border-soft bg-white md:col-span-2" />
          <textarea
            placeholder={t("b2b.message")}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            rows={4}
            className="md:col-span-2 border border-soft bg-white p-3 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-brand"
            data-testid="b2b-message"
          />
          <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
            <input
              type="checkbox"
              required
              checked={form.privacy_consent}
              onChange={(e) => set("privacy_consent", e.target.checked)}
              className="mt-1"
              data-testid="b2b-privacy"
            />
            <span>{t("common.privacy_consent")}</span>
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="b2b-submit"
              className="rounded-full bg-brand text-white px-8 py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50 transition-colors"
            >
              {loading ? t("common.sending") : t("common.submit")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
